import { FetchResult } from '@apollo/client'
import { useState } from 'react'
import {
    DeleteNodeMutation,
    useDeleteNodeMutation
} from '../../../apollo/configurator/mutations/DeleteNode.generated'
import { RootNodesQuery } from '../../../apollo/configurator/queries/RootNodes.generated'

type ModelNode = RootNodesQuery['configuratorNodes'][0]

interface UseModelSelectionProps {
    paginatedModels: ModelNode[]
    filteredAndSortedModels: ModelNode[]
    refetch?: () => void
    allModels?: ModelNode[]
}

interface DeleteResult {
    modelId: string
    success: boolean
    result?: FetchResult<DeleteNodeMutation>
    error?: unknown
}

interface UseModelSelectionReturn {
    selected: string[]
    setSelected: (value: string[]) => void
    handleSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void
    handleSelectClick: (id: string) => void
    isSelected: (id: string) => boolean
    handleBulkDelete: () => void
    isDeleting: boolean
    showConfirmDialog: boolean
    selectedModelNames: string[]
    handleConfirmDelete: () => void
    handleCancelDelete: () => void
}

export const useModelSelection = ({
    paginatedModels,
    filteredAndSortedModels,
    refetch,
    allModels = []
}: UseModelSelectionProps): UseModelSelectionReturn => {
    const [selected, setSelected] = useState<string[]>([])
    const [isDeleting, setIsDeleting] = useState(false)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [selectedModelNames, setSelectedModelNames] = useState<string[]>([])
    const [deleteNode] = useDeleteNodeMutation()

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = paginatedModels.map((model) => model.id.toString())
            setSelected(newSelected)
        } else {
            setSelected([])
        }
    }

    const handleSelectClick = (id: string) => {
        const selectedIndex = selected.indexOf(id)
        let newSelected: string[] = []

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id)
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1))
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1))
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            )
        }

        setSelected(newSelected)
    }

    const isSelected = (id: string) => selected.indexOf(id) !== -1

    const handleBulkDelete = () => {
        if (selected.length === 0) return

        const modelNames = selected.map((id) => {
            const model = allModels.find((m) => m.id === id)
            return model?.code || `Model ${id}`
        })

        setSelectedModelNames(modelNames)
        setShowConfirmDialog(true)
    }

    const handleCancelDelete = () => {
        setShowConfirmDialog(false)
        setSelectedModelNames([])
    }

    const handleConfirmDelete = async () => {
        setShowConfirmDialog(false)

        try {
            setIsDeleting(true)

            const results: DeleteResult[] = []

            for (const modelId of selected) {
                try {
                    const result = await deleteNode({
                        variables: {
                            id: modelId
                        }
                    })

                    results.push({ modelId, success: true, result })
                } catch (error) {
                    results.push({ modelId, success: false, error })
                }
            }

            if (refetch) {
                refetch()
            }

            setSelected([])
        } catch (error) {
            console.error('Error during bulk delete:', error)
        } finally {
            setIsDeleting(false)
            setSelectedModelNames([])
        }
    }

    return {
        selected,
        setSelected,
        handleSelectAllClick,
        handleSelectClick,
        isSelected,
        handleBulkDelete,
        isDeleting,
        showConfirmDialog,
        selectedModelNames,
        handleConfirmDelete,
        handleCancelDelete
    }
}
